import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Button,
  Chip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Block,
  CheckCircle,
  Warning as WarningIcon,
  Email,
  Phone,
  School,
  LocationOn,
  Report,
  Star,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface UserDetails {
  userId: string;
  fullname: string;
  email: string;
  profileImage: string;
  schoolName: string;
  schoolDepartment: string;
  schoolYear: string;
  city: string;
  phoneNumber: string;
  gender: string;
  biography: string;
  isBanned: boolean;
  isActive: boolean;
  isOfficial: boolean;
  isPremium?: boolean;
  createdAt: string;
  interests: string[];
}

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [warningDialog, setWarningDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [premiumDialog, setPremiumDialog] = useState(false);
  const [warningTitle, setWarningTitle] = useState('Uyarı');
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getUserDetails(userId!);
      
      if (response.status) {
        // Backend farklı formatlarda dönebilir
        const userData = response.data?.user || response.data;
        setUser(userData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kullanıcı bilgileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendWarning = async () => {
    if (!warningMessage.trim()) return;
    
    setActionLoading(true);
    try {
      await adminService.sendWarning({
        userId: userId!,
        warningTitle,
        warningMessage,
      });
      setWarningDialog(false);
      setWarningMessage('');
      alert('Uyarı başarıyla gönderildi!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Uyarı gönderilirken hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBanUser = async () => {
    setActionLoading(true);
    try {
      await adminService.banUser(userId!, !user?.isBanned);
      await fetchUserDetails();
      alert(user?.isBanned ? 'Kullanıcının banı kaldırıldı!' : 'Kullanıcı banlandı!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'İşlem sırasında hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setActionLoading(true);
    try {
      await adminService.deleteUser(userId!);
      alert('Kullanıcı başarıyla silindi!');
      navigate('/admin/users');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Kullanıcı silinirken hata oluştu.');
    } finally {
      setActionLoading(false);
      setDeleteDialog(false);
    }
  };

  const handleGivePremium = async () => {
    setActionLoading(true);
    try {
      await adminService.givePremiumToUser(userId!);
      alert('Kullanıcıya premium başarıyla verildi!');
      setPremiumDialog(false);
      await fetchUserDetails(); // Kullanıcıyı yeniden yükle
    } catch (err: any) {
      alert(err.response?.data?.message || 'Premium verilirken hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Kullanıcı bulunamadı.'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/users')} sx={{ mt: 2 }}>
          Geri Dön
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <IconButton onClick={() => navigate('/admin/users')} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Kullanıcı Detayları
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                src={user.profileImage}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
              >
                {user.fullname?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                {user.fullname || 'İsimsiz Kullanıcı'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.userId || '-'}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                {user.isBanned ? (
                  <Chip icon={<Block />} label="Banlı" color="error" />
                ) : user.isActive ? (
                  <Chip icon={<CheckCircle />} label="Aktif" color="success" />
                ) : (
                  <Chip icon={<WarningIcon />} label="Pasif" color="warning" />
                )}
                {user.isOfficial && <Chip label="Resmi" color="info" />}
                {user.isPremium && <Chip icon={<Star />} label="Premium" color="warning" sx={{ bgcolor: '#ffd700', color: '#000' }} />}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Report />}
                  onClick={() => navigate(`/admin/reports/user/${userId}`)}
                  fullWidth
                  color="info"
                >
                  Kullanıcı Raporları
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WarningIcon />}
                  onClick={() => setWarningDialog(true)}
                  fullWidth
                >
                  Uyarı Gönder
                </Button>
                {!user.isPremium && (
                  <Button
                    variant="outlined"
                    startIcon={<Star />}
                    onClick={() => setPremiumDialog(true)}
                    fullWidth
                    sx={{ 
                      color: '#ffd700', 
                      borderColor: '#ffd700',
                      '&:hover': {
                        borderColor: '#ffed4e',
                        bgcolor: 'rgba(255, 215, 0, 0.08)'
                      }
                    }}
                  >
                    Premium Ver
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color={user.isBanned ? 'success' : 'warning'}
                  startIcon={<Block />}
                  onClick={handleBanUser}
                  disabled={actionLoading}
                  fullWidth
                >
                  {user.isBanned ? 'Banı Kaldır' : 'Banla'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  fullWidth
                >
                  Kullanıcıyı Sil
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Details Card */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Kullanıcı Bilgileri
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Email color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2">{user.email || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Phone color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Telefon
                    </Typography>
                    <Typography variant="body2">{user.phoneNumber || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <School color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Okul
                    </Typography>
                    <Typography variant="body2">{user.schoolName || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Şehir
                    </Typography>
                    <Typography variant="body2">{user.city || '-'}</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Bölüm
                </Typography>
                <Typography variant="body2">{user.schoolDepartment || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Sınıf
                </Typography>
                <Typography variant="body2">{user.schoolYear || '-'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Biyografi
                </Typography>
                <Typography variant="body2">{user.biography || 'Henüz biyografi eklenmemiş.'}</Typography>
              </Grid>

              {user.interests && user.interests.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    İlgi Alanları
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {user.interests.map((interest, index) => (
                      <Chip key={index} label={interest} size="small" />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Kayıt Tarihi
                </Typography>
                <Typography variant="body2">
                  {user.createdAt ? new Date(user.createdAt).toLocaleString('tr-TR') : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Warning Dialog */}
      <Dialog open={warningDialog} onClose={() => setWarningDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kullanıcıya Uyarı Gönder</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Uyarı Başlığı"
            value={warningTitle}
            onChange={(e) => setWarningTitle(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Uyarı Mesajı"
            multiline
            rows={4}
            value={warningMessage}
            onChange={(e) => setWarningMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialog(false)}>İptal</Button>
          <Button
            onClick={handleSendWarning}
            variant="contained"
            disabled={!warningMessage.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Gönder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Kullanıcıyı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>İptal</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Premium Dialog */}
      <Dialog open={premiumDialog} onClose={() => setPremiumDialog(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: '#ffd700' }} />
            <span>Premium Ver</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Bu kullanıcıya premium üyelik vermek istediğinizden emin misiniz?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Premium üyelik kullanıcıya özel özellikler ve avantajlar sağlayacaktır.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPremiumDialog(false)}>İptal</Button>
          <Button
            onClick={handleGivePremium}
            variant="contained"
            disabled={actionLoading}
            sx={{ 
              bgcolor: '#ffd700', 
              color: '#000',
              '&:hover': {
                bgcolor: '#ffed4e'
              }
            }}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Premium Ver'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDetail;

